import React from 'react';
import Paragraph from 'antd/lib/typography/Paragraph';
import {
	CheckOutlined,
	CloseOutlined,
	PaperClipOutlined,
} from '@ant-design/icons';
import { Grid } from 'antd';
import { toMoment } from '@shared/utils/date';
import { standardSort } from '@shared/utils/standardSort';
import { formatCurrency } from '@shared/utils/currency';
import {
	AccountingItemApprovalStates,
	ExpenseStatus,
	FixtureTypeLabels,
	FixtureTypes,
	HireInvoiceItemStates,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { sanitizeNumber } from '@shared/utils/number';
import type { TransformedExpenses } from '@api/utils/getTransformedVoyageExpenses';
import { VoyageExpensePaymentProps } from '@api/models/voyage-expense-payment';
import StateTag from '@client/components/StateTag';
import VendorExpensePaymentStateTag, { calculateTotalPayment } from '@client/components/VendorExpensePaymentStateTag';
import ApprovalStateTag from '@client/components/ApprovalStateTag/ApprovalStateTag';
import { getFilterProps } from '@client/utils/table';
import styles from '../ExpensesScreen/components/styles/ExpenseScreen.module.css';

const useGetVendorExpensesTableColumns = (
	filterMaps: {
		voyages: Array<string>;
	}
) => {
	const screens = Grid.useBreakpoint();

	const getPaidAmount = (r: TransformedExpenses): number =>
		calculateTotalPayment(r.VoyageExpensePayments);

	const getOutstandingAmount = (r: TransformedExpenses): number =>
		sanitizeNumber(r.amount ?? 0) - getPaidAmount(r);

	const numberSorter = (getValue: (r: TransformedExpenses) => number) =>
		(a: TransformedExpenses, b: TransformedExpenses) => getValue(a) - getValue(b);

	const calculatePaidAmount = (
		voyageExpensePayments: VoyageExpensePaymentProps[],
		record: TransformedExpenses,
	) => {
		if (voyageExpensePayments.length > 0 && Array.isArray(voyageExpensePayments)) {
			const totalAmount = voyageExpensePayments.reduce((sum, payment) => {
				const amount = Number(payment.amount);

				return sum + amount;
			}, 0);

			const formattedTotal = formatCurrency(
				totalAmount,
				record.currency,
				{ forceDecimals: true },
			);

			return formattedTotal;
		}

		return '-';
	};

	const calculateOutstandingAmount = (
		voyageExpensePayments: VoyageExpensePaymentProps[],
		record: TransformedExpenses,
	) => {
		if (voyageExpensePayments.length > 0 && Array.isArray(voyageExpensePayments)) {
			const totalAmount = voyageExpensePayments.reduce((sum, payment) => {
				const amount = Number(payment.amount);

				return sum + amount;
			}, 0);

			const formattedTotal = formatCurrency(
				record.amount - totalAmount,
				record.currency,
				{ forceDecimals: true },
			);

			return formattedTotal;
		}

		if (record.amount == null) {
			return '-';
		}

		const total = formatCurrency(
			record.amount,
			record.currency,
			{ forceDecimals: true },
		);

		return record.amount !== 0 ? total : '-';
	};

	const vesselColumns = [
		{
			title: 'Description',
			dataIndex: 'itemDescription',
			key: 'itemDescription',
			defaultSortOrder: 'ascend',
			render: (v: any) => {
				if (!v) {
					return '-';
				}

				return (
					<Paragraph style={{ margin: 0 }} ellipsis={{ tooltip: v }}>
						{v}
					</Paragraph>
				);
			},
		},
		{
			title: 'Supplier',
			dataIndex: 'supplierName',
			key: 'supplierName',
			render: (v: any) => {
				if (!v) {
					return '-';
				}

				return (
					<Paragraph style={{ margin: 0 }} ellipsis={{ tooltip: v }}>
						{v}
					</Paragraph>
				);
			},
		},
		{
			title: 'Vessel',
			dataIndex: 'vesselName',
			key: 'vessel',
			render: (v: any) => (
				<Paragraph style={{ margin: 0 }} ellipsis={{ tooltip: v }}>
					{v}
				</Paragraph>
			),
		},
		{
			width: 95,
			title: 'Contract',
			dataIndex: 'voyageIdentifier',
			...getFilterProps(filterMaps.voyages.map((v) => ({ voyageIdentifier: v })), 'select', 'voyageIdentifier', 'desc'),
		},
		...(!screens.xxl ? [] : [
			{
				title: 'Contract type',
				dataIndex: 'fixtureType',
				key: 'fixtureType',
				render: (v: Values<typeof FixtureTypes>) => (
					<Paragraph style={{ margin: 0 }} ellipsis={{ tooltip: v }}>
						{FixtureTypeLabels[v]}
					</Paragraph>
				),
			},
		]),
		{
			width: 80,
			title: 'Id',
			dataIndex: 'customInvoiceId',
			key: 'customInvoiceId',
			render: (v: any) => (v ? (
				<Paragraph style={{ margin: 0, padding: 0 }} ellipsis={{ tooltip: v }}>
					<b>
						{v}
					</b>
				</Paragraph>
			) :
				(<b>-</b>)),
		},
		...(!screens.xxl ? [] : [
			{
				width: 125,
				title: 'Invoice Date',
				dataIndex: 'invoiceDate',
				key: 'invoiceDate',
				render: (v: string) => (
					v ?? '-'
				),
				sorter: (a: TransformedExpenses, b: TransformedExpenses) => {
					if (a.invoiceDate == null) {
						return 1;
					}

					if (b.invoiceDate == null) {
						return -1;
					}

					return toMoment(a.invoiceDate).unix() - toMoment(b.invoiceDate).unix();
				},
			},
		]),
		{
			title: 'Due date',
			dataIndex: 'dueDate',
			key: 'dueDate',
			render: (v: any) => {
				if (!v) {
					return '-';
				}

				return (
					<Paragraph style={{ margin: 0 }} ellipsis={{ tooltip: v }}>
						{v}
					</Paragraph>
				);
			},
		},
		{
			align: 'right',
			title: 'Total',
			dataIndex: 'amount',
			key: 'amount',
			render: (v: any, record: any) => (v ? (
				formatCurrency(v, record.currency, { forceDecimals: true })
			) : '-'),
			sorter: standardSort('amount'),
		},
		{
			align: 'right',
			title: 'Paid',
			dataIndex: 'VoyageExpensePayments',
			key: 'VoyageExpensePayments',
			render: (voyageExpensePayments: any, record: any) => {
				const paidAmount = calculatePaidAmount(voyageExpensePayments, record);

				return paidAmount;
			},
			// Cant use standard sort as the amount is calculated from the payments,
			// so we need to calculate the total paid amount for sorting
			sorter: numberSorter(getPaidAmount),
		},
		{
			align: 'right',
			title: 'Outstanding',
			dataIndex: 'VoyageExpensePayments',
			key: 'outstanding',
			render: (voyageExpensePayments: any, record: any) => {
				const totalOutstanding = calculateOutstandingAmount(voyageExpensePayments, record);

				return totalOutstanding;
			},
			// Cant use standard sort as the amount is calculated from the payments,
			// so we need to calculate the total outstanding amount for sorting
			sorter: numberSorter(getOutstandingAmount),
		},
		...(!screens.xxl ? [] : [
			{
				width: 100,
				title: 'Rebillable',
				dataIndex: ['receivableStatus'],
				...getFilterProps([
					HireInvoiceItemStates.PENDING,
					HireInvoiceItemStates.INVOICED,
					'Not Created'
				], 'select', 'receivableStatus', undefined, true),
				key: 'receivableStatus',
				render: (receivableStatus: Values<typeof HireInvoiceItemStates>) => (
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<StateTag
							className={styles.statusTag}
							state={receivableStatus}
						/>
					</div>
				)
			},
		]),
		...(!screens.xxl ? [] : [
			{
				width: 95,
				title: 'Payment',
				dataIndex: ['paymentStatus'],
				key: 'paymentStatus',
				...getFilterProps(
					[
						ExpenseStatus.OVERDUE,
						ExpenseStatus.OVERPAID,
						ExpenseStatus.PAID,
						ExpenseStatus.PARTIAL,
						ExpenseStatus.PENDING,
					],
					'select',
					'paymentStatus',
					undefined,
					true,
				),
				render: (
					_paymentStatus: ExpenseStatus, record: TransformedExpenses & { paymentStatus?: ExpenseStatus }) => (
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<VendorExpensePaymentStateTag
							state={record.paymentStatus}
							className={styles.statusTag}
						/>
					</div>
				),
			},
			{
				width: 95,
				title: 'Approval',
				dataIndex: 'state',
				key: 'state',
				filters: [
					{ text: 'Pending', value: AccountingItemApprovalStates.PENDING },
					{ text: 'Submitted', value: AccountingItemApprovalStates.SUBMITTED },
					{ text: 'Approved', value: AccountingItemApprovalStates.APPROVED },
					{ text: 'Rejected', value: AccountingItemApprovalStates.REJECTED },
				],
				filterMultiple: true,
				onFilter: (value: AccountingItemApprovalStates,
					record: TransformedExpenses & { state?: AccountingItemApprovalStates }) => record.state === value,
				render: (state: AccountingItemApprovalStates) => (
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<ApprovalStateTag state={state} />
					</div>
				),
			}
		]),
		{
			width: 35,
			align: 'right',
			title: '',
			dataIndex: 'attachments',
			key: 'attachments',
			render: (attachment: any) => {
				return (attachment.length > 0 ? (<PaperClipOutlined />) : '-');
			},
			filterMultiple: false,
			filters: [
				{
					text: (
						<span>
							<CheckOutlined style={{ color: 'green', marginRight: 4 }} />
							Has Attachments
						</span>
					),
					value: true,
				},
				{
					text: (
						<span>
							<CloseOutlined style={{ color: 'red', marginRight: 4 }} />
							No Attachments
						</span>
					),
					value: false,
				},
			],
		},
	];

	return vesselColumns;
};

export default useGetVendorExpensesTableColumns;
